import { useState, useEffect, useCallback, useRef } from 'react';
import { testStudentApi } from '../api';
import TakeTest from './TakeTest';

// ── country codes ─────────────────────────────────────────────────────────────
const COUNTRY_CODES = [
  { code: '91',  label: '🇮🇳 +91  India' },
  { code: '1',   label: '🇺🇸 +1   USA / Canada' },
  { code: '44',  label: '🇬🇧 +44  UK' },
  { code: '61',  label: '🇦🇺 +61  Australia' },
  { code: '971', label: '🇦🇪 +971 UAE' },
  { code: '966', label: '🇸🇦 +966 Saudi Arabia' },
  { code: '974', label: '🇶🇦 +974 Qatar' },
  { code: '965', label: '🇰🇼 +965 Kuwait' },
  { code: '968', label: '🇴🇲 +968 Oman' },
  { code: '973', label: '🇧🇭 +973 Bahrain' },
  { code: '60',  label: '🇲🇾 +60  Malaysia' },
  { code: '65',  label: '🇸🇬 +65  Singapore' },
  { code: '64',  label: '🇳🇿 +64  New Zealand' },
  { code: '27',  label: '🇿🇦 +27  South Africa' },
  { code: '49',  label: '🇩🇪 +49  Germany' },
  { code: '33',  label: '🇫🇷 +33  France' },
  { code: '39',  label: '🇮🇹 +39  Italy' },
  { code: '34',  label: '🇪🇸 +34  Spain' },
  { code: '31',  label: '🇳🇱 +31  Netherlands' },
  { code: '81',  label: '🇯🇵 +81  Japan' },
  { code: '82',  label: '🇰🇷 +82  South Korea' },
  { code: '86',  label: '🇨🇳 +86  China' },
  { code: '55',  label: '🇧🇷 +55  Brazil' },
  { code: '52',  label: '🇲🇽 +52  Mexico' },
];

function WaPhoneInput({ country, setCountry, phone, setPhone, onEnter, style }) {
  return (
    <div style={{ display: 'flex', gap: 4, ...style }}>
      <select
        value={country}
        onChange={e => setCountry(e.target.value)}
        style={{ fontSize: 12, padding: '4px 4px', borderRadius: 6, border: '1px solid #cbd5e0', background: '#fff', flexShrink: 0 }}
      >
        {COUNTRY_CODES.map(c => (
          <option key={c.code} value={c.code}>{c.label}</option>
        ))}
      </select>
      <input
        type="tel"
        className="form-control"
        placeholder="Phone number"
        value={phone}
        onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 15))}
        onKeyDown={e => e.key === 'Enter' && onEnter && onEnter()}
        style={{ fontSize: 12, padding: '4px 8px', minWidth: 0, flex: 1 }}
      />
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────────

function gradeStyle(grade) {
  if (!grade) return {};
  const g = grade.toUpperCase();
  if (g === 'A+') return { background: '#fef3c7', color: '#92400e', fontWeight: 700 };
  if (g === 'A')  return { background: '#f0fff4', color: '#22863a', fontWeight: 700 };
  if (g === 'B')  return { background: '#ebf8ff', color: '#0066CC', fontWeight: 700 };
  if (g === 'C')  return { background: '#fffaf0', color: '#c05621', fontWeight: 700 };
  return { background: '#fff5f5', color: '#c53030', fontWeight: 700 };
}

// ── TEST CARD ─────────────────────────────────────────────────────────────────

function TestCard({ test, onStart, starting }) {
  const { attemptStatus, allowRetake } = test;

  const isDone = attemptStatus === 'SUBMITTED' || attemptStatus === 'TIMED_OUT';

  let btnLabel, btnClass, btnDisabled;
  if (isDone && !allowRetake) {
    btnLabel = 'Completed'; btnClass = 'btn btn-outline btn-sm'; btnDisabled = true;
  } else if (isDone && allowRetake) {
    btnLabel = 'Retake'; btnClass = 'btn btn-warning btn-sm'; btnDisabled = starting;
  } else if (attemptStatus === 'IN_PROGRESS') {
    btnLabel = 'Resume'; btnClass = 'btn btn-primary btn-sm'; btnDisabled = starting;
  } else {
    btnLabel = 'Start Test'; btnClass = 'btn btn-success btn-sm'; btnDisabled = starting;
  }

  let statusBadge = null;
  if (attemptStatus === 'SUBMITTED')        statusBadge = <span className="badge badge-green" style={{ marginLeft: 8 }}>Done</span>;
  else if (attemptStatus === 'TIMED_OUT')   statusBadge = <span className="badge badge-orange" style={{ marginLeft: 8 }}>Timed Out</span>;
  else if (attemptStatus === 'IN_PROGRESS') statusBadge = <span className="badge badge-blue" style={{ marginLeft: 8 }}>In Progress</span>;

  return (
    <div className="card" style={{ margin: 0, display: 'flex', flexDirection: 'column' }}>
      <div className="card-body" style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#1a202c', marginBottom: 8 }}>
          {test.name}
          {statusBadge}
        </div>
        {test.subjectName && (
          <span className="badge badge-blue" style={{ marginBottom: 10, display: 'inline-block' }}>{test.subjectName}</span>
        )}
        {test.className && <span style={{ fontSize: 11, color: '#718096', display: 'block' }}>🏫 {test.className}</span>}
        <div style={{ fontSize: 12, color: '#718096', marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <span><i className="fas fa-question-circle" style={{ marginRight: 4 }}></i>{test.numQuestions} questions</span>
          <span><i className="fas fa-clock" style={{ marginRight: 4 }}></i>{test.timeLimitMinutes} min</span>
          {test.totalMarks && <span><i className="fas fa-star" style={{ marginRight: 4 }}></i>{test.totalMarks} marks</span>}
        </div>
        <button className={btnClass} onClick={() => !btnDisabled && onStart(test.id)} disabled={btnDisabled} style={{ width: '100%', justifyContent: 'center' }}>
          {starting ? 'Starting…' : btnLabel}
        </button>
      </div>
    </div>
  );
}

// ── DASHBOARD VIEW ────────────────────────────────────────────────────────────

function DashboardView({ student, onStartTest, onReview }) {
  const [availableTests, setAvailableTests] = useState([]);
  const [myResults, setMyResults]           = useState([]);
  const [testsLoading, setTestsLoading]     = useState(true);
  const [resultsLoading, setResultsLoading] = useState(true);
  const [testsErr, setTestsErr]             = useState('');
  const [resultsErr, setResultsErr]         = useState('');
  const [starting, setStarting]             = useState(false);
  const [emailOpenId, setEmailOpenId]       = useState(null);
  const [emailAddr, setEmailAddr]           = useState('');
  const [emailSending, setEmailSending]     = useState(false);
  const [emailStatus, setEmailStatus]       = useState('');
  const [waOpenId, setWaOpenId]             = useState(null);
  const [waPhone, setWaPhone]               = useState('');
  const [waCountry, setWaCountry]           = useState('91');
  const [waSending, setWaSending]           = useState(false);
  const [waStatus, setWaStatus]             = useState('');

  const loadTests = useCallback(() => {
    setTestsLoading(true);
    setTestsErr('');
    testStudentApi.getAvailableTests()
      .then(setAvailableTests)
      .catch(e => setTestsErr(e.message))
      .finally(() => setTestsLoading(false));
  }, []);

  const loadResults = useCallback(() => {
    setResultsLoading(true);
    testStudentApi.getMyResults()
      .then(setMyResults)
      .catch(e => setResultsErr(e.message))
      .finally(() => setResultsLoading(false));
  }, []);

  const toggleEmail = (id) => {
    setEmailOpenId(prev => prev === id ? null : id);
    setEmailAddr('');
    setEmailStatus('');
    setWaOpenId(null);
  };

  const toggleWa = (id) => {
    setWaOpenId(prev => prev === id ? null : id);
    setWaPhone('');
    setWaCountry('91');
    setWaStatus('');
    setEmailOpenId(null);
  };

  const handleEmailResult = async (r) => {
    if (!emailAddr.trim()) { setEmailStatus('Enter an email address.'); return; }
    setEmailSending(true);
    setEmailStatus('');
    const pct     = r.percentage != null ? Math.round(parseFloat(r.percentage)) : 0;
    const subject = `My Results — ${r.testName || 'Test'} — SmaBizZ`;
    const body    = buildEmailHtml(r, student?.name, pct);
    const params  = new URLSearchParams({ to: emailAddr.trim(), subject, body });
    try {
      const res = await fetch('/spring/sendemail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
        credentials: 'include',
      });
      setEmailStatus(res.ok ? '✓ Sent!' : '✗ Failed. Try again.');
    } catch {
      setEmailStatus('✗ Network error.');
    } finally {
      setEmailSending(false);
    }
  };

  const handleWhatsAppSend = async (r) => {
    if (!waPhone.trim()) { setWaStatus('Enter a mobile number.'); return; }
    setWaSending(true);
    setWaStatus('');
    try {
      const res = await fetch('/spring/school-api/users/whatsapp-result', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          mobileNumber:  waCountry + waPhone.trim(),
          studentName:   student?.name || 'Student',
          testName:      r.testName    || 'Test',
          score:         r.score       ?? 0,
          totalMarks:    r.totalMarks  ?? 0,
          percentage:    r.percentage  ?? 0,
          grade:         r.grade       || '',
          correctCount:  r.correctCount  ?? 0,
          wrongCount:    r.wrongCount    ?? 0,
          skippedCount:  r.skippedCount  ?? 0,
        }),
      });
      const data = await res.json().catch(() => ({}));
      setWaStatus(res.ok ? '✓ WhatsApp message sent!' : '✗ ' + (data.error || 'Failed. Try again.'));
    } catch {
      setWaStatus('✗ Network error.');
    } finally {
      setWaSending(false);
    }
  };

  useEffect(() => { loadTests(); loadResults(); }, [loadTests, loadResults]);

  const handleStart = async (testId) => {
    setStarting(true);
    setTestsErr('');
    try {
      const data = await testStudentApi.startTest(testId);
      onStartTest(data.attemptId);
    } catch (e) {
      setTestsErr(e.message);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1a202c' }}>
          <i className="fas fa-user-graduate" style={{ marginRight: 8, color: '#0066CC' }}></i>
          Welcome, {student?.name || 'Student'}
        </div>
      </div>

      {/* Available Tests */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <div className="card-title"><i className="fas fa-list-alt" style={{ marginRight: 8, color: '#0066CC' }}></i>Available Tests</div>
          <button className="btn btn-outline btn-sm" onClick={loadTests}><i className="fas fa-sync-alt"></i></button>
        </div>
        <div className="card-body">
          {testsErr && <div className="error-msg">{testsErr}</div>}
          {testsLoading ? <div className="loading-msg">Loading tests…</div> : (
            availableTests.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#718096', padding: 32, fontSize: 14 }}>
                <i className="fas fa-inbox" style={{ fontSize: 32, marginBottom: 12, display: 'block', color: '#cbd5e0' }}></i>
                No tests available at this time.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                {availableTests.map(t => (
                  <TestCard key={t.id} test={t} onStart={handleStart} starting={starting} />
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {/* My Results */}
      <div className="card">
        <div className="card-header">
          <div className="card-title"><i className="fas fa-chart-bar" style={{ marginRight: 8, color: '#22863a' }}></i>My Results</div>
          <button className="btn btn-outline btn-sm" onClick={loadResults}><i className="fas fa-sync-alt"></i></button>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {resultsErr && <div className="error-msg" style={{ margin: 16 }}>{resultsErr}</div>}
          {resultsLoading ? <div className="loading-msg">Loading results…</div> : (
            myResults.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#718096', padding: 32, fontSize: 14 }}>
                <i className="fas fa-clipboard-list" style={{ fontSize: 32, marginBottom: 12, display: 'block', color: '#cbd5e0' }}></i>
                No results yet. Take a test to see your score here.
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Test</th><th>Subject</th><th>Score</th><th>/Total</th><th>%</th>
                      <th>Grade</th><th>Correct</th><th>Wrong</th><th>Skipped</th><th>Date</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myResults.map((r, i) => (
                      <tr key={r.attemptId || i}>
                        <td><strong>{r.testName}</strong></td>
                        <td>{r.subjectName || '—'}</td>
                        <td>{r.score}</td>
                        <td>{r.totalMarks}</td>
                        <td>{r.percentage != null ? Math.round(parseFloat(r.percentage)) : '—'}%</td>
                        <td><span className="badge" style={gradeStyle(r.grade)}>{r.grade || '—'}</span></td>
                        <td style={{ color: '#22863a', fontWeight: 600 }}>{r.correctCount ?? '—'}</td>
                        <td style={{ color: '#c53030', fontWeight: 600 }}>{r.wrongCount ?? '—'}</td>
                        <td style={{ color: '#a0aec0' }}>{r.skippedCount ?? '—'}</td>
                        <td style={{ fontSize: 12, color: '#718096' }}>{r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : '—'}</td>
                        <td style={{ minWidth: 180 }}>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                            <button className="btn btn-outline btn-sm" onClick={() => onReview(r.attemptId)} title="Review questions and answers">
                              📋 Review
                            </button>
                            <button className="btn btn-outline btn-sm" onClick={() => toggleEmail(r.attemptId || i)} title="Email results">
                              📧 Email
                            </button>
                            <button
                              onClick={() => toggleWa(r.attemptId || i)}
                              style={{ background: '#25D366', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                              title="Send via WhatsApp"
                            >
                              💬 WhatsApp
                            </button>
                          </div>
                          {emailOpenId === (r.attemptId || i) && (
                            <div style={{ marginTop: 6 }}>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <input
                                  type="email"
                                  className="form-control"
                                  placeholder="Email address"
                                  value={emailAddr}
                                  onChange={e => setEmailAddr(e.target.value)}
                                  onKeyDown={e => e.key === 'Enter' && handleEmailResult(r)}
                                  style={{ fontSize: 12, padding: '4px 8px', minWidth: 0, flex: 1 }}
                                />
                                <button className="btn btn-primary btn-sm" onClick={() => handleEmailResult(r)} disabled={emailSending}>
                                  {emailSending ? '…' : 'Send'}
                                </button>
                              </div>
                              {emailStatus && (
                                <div style={{ fontSize: 11, marginTop: 4, fontWeight: 600, color: emailStatus.startsWith('✓') ? '#22863a' : '#c53030' }}>
                                  {emailStatus}
                                </div>
                              )}
                            </div>
                          )}
                          {waOpenId === (r.attemptId || i) && (
                            <div style={{ marginTop: 6 }}>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <WaPhoneInput
                                  country={waCountry} setCountry={setWaCountry}
                                  phone={waPhone} setPhone={setWaPhone}
                                  onEnter={() => handleWhatsAppSend(r)}
                                  style={{ flex: 1 }}
                                />
                                <button
                                  onClick={() => handleWhatsAppSend(r)}
                                  disabled={waSending}
                                  style={{ background: '#25D366', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                >
                                  {waSending ? '…' : 'Send'}
                                </button>
                              </div>
                              {waStatus && (
                                <div style={{ fontSize: 11, marginTop: 4, fontWeight: 600, color: waStatus.startsWith('✓') ? '#22863a' : '#c53030' }}>
                                  {waStatus}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ── HELPERS ───────────────────────────────────────────────────────────────────

function optionText(q, letter) {
  if (!letter || !q) return null;
  return { A: q.optionA, B: q.optionB, C: q.optionC, D: q.optionD }[letter] || null;
}

function buildEmailHtml(result, studentName, pct) {
  const name   = studentName || 'Student';
  const test   = result?.testName || 'Test';
  const score  = result?.score ?? 0;
  const total  = result?.totalMarks ?? 0;
  const grade  = result?.grade || '—';
  const status = result?.status === 'TIMED_OUT' ? 'Time Expired' : 'Submitted';
  return `Hi ${name},\n\nHere are your test results from SmaBizZ:\n\nTest: ${test}\nScore: ${score} / ${total}\nPercentage: ${pct}%\nGrade: ${grade}\nStatus: ${status}\n\nBreakdown:\n  Correct : ${result?.correctCount ?? 0}\n  Wrong   : ${result?.wrongCount ?? 0}\n  Skipped : ${result?.skippedCount ?? 0}\n\nKeep up the great work!\n\n— SmaBizZ School Management`;
}

// ── SCORE SUMMARY CARD ────────────────────────────────────────────────────────

function ScoreSummary({ result, onBack, backLabel = 'Back to Tests', studentName }) {
  const [emailOpen,    setEmailOpen]    = useState(false);
  const [emailAddr,    setEmailAddr]    = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus,  setEmailStatus]  = useState('');
  const [waOpen,    setWaOpen]    = useState(false);
  const [waPhone,   setWaPhone]   = useState('');
  const [waCountry, setWaCountry] = useState('91');
  const [waSending, setWaSending] = useState(false);
  const [waStatus,  setWaStatus]  = useState('');
  const [fullWaOpen,    setFullWaOpen]    = useState(false);
  const [fullWaPhone,   setFullWaPhone]   = useState('');
  const [fullWaCountry, setFullWaCountry] = useState('91');
  const [fullWaSending, setFullWaSending] = useState(false);
  const [fullWaStatus,  setFullWaStatus]  = useState('');

  const pct   = result?.percentage != null ? Math.round(parseFloat(result.percentage)) : 0;
  const grade = result?.grade;

  const handleEmail = async () => {
    if (!emailAddr.trim()) { setEmailStatus('Please enter an email address.'); return; }
    setEmailSending(true);
    setEmailStatus('');
    const subject = `My Results — ${result?.testName || 'Test'} — SmaBizZ`;
    const body    = buildEmailHtml(result, studentName, pct);
    const params  = new URLSearchParams({ to: emailAddr.trim(), subject, body });
    try {
      const res = await fetch('/spring/sendemail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
        credentials: 'include',
      });
      setEmailStatus(res.ok ? '✓ Email sent successfully!' : '✗ Failed to send email. Please try again.');
    } catch {
      setEmailStatus('✗ Network error. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };

  const handleFullReview = async () => {
    if (!fullWaPhone.trim()) { setFullWaStatus('Enter a mobile number.'); return; }
    setFullWaSending(true);
    setFullWaStatus('');
    try {
      const res = await fetch('/spring/school-api/users/whatsapp-review-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          mobileNumber: fullWaCountry + fullWaPhone.trim(),
          studentName:  studentName || 'Student',
          result: {
            testName:      result?.testName   || 'Test',
            score:         result?.score      ?? 0,
            totalMarks:    result?.totalMarks ?? 0,
            percentage:    result?.percentage ?? 0,
            grade:         result?.grade      || '',
            correctCount:  result?.correctCount  ?? 0,
            wrongCount:    result?.wrongCount    ?? 0,
            skippedCount:  result?.skippedCount  ?? 0,
          },
          questions: result?.questions || [],
        }),
      });
      const data = await res.json().catch(() => ({}));
      setFullWaStatus(res.ok ? '✓ WhatsApp sent! Tap the link to view full review.' : '✗ ' + (data.error || 'Failed.'));
    } catch {
      setFullWaStatus('✗ Network error.');
    } finally {
      setFullWaSending(false);
    }
  };

  const handleWhatsApp = async () => {
    if (!waPhone.trim()) { setWaStatus('Enter a mobile number.'); return; }
    setWaSending(true);
    setWaStatus('');
    try {
      const res = await fetch('/spring/school-api/users/whatsapp-result', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          mobileNumber:  waCountry + waPhone.trim(),
          studentName:   studentName || 'Student',
          testName:      result?.testName   || 'Test',
          score:         result?.score      ?? 0,
          totalMarks:    result?.totalMarks ?? 0,
          percentage:    result?.percentage ?? 0,
          grade:         result?.grade      || '',
          correctCount:  result?.correctCount  ?? 0,
          wrongCount:    result?.wrongCount    ?? 0,
          skippedCount:  result?.skippedCount  ?? 0,
        }),
      });
      const data = await res.json().catch(() => ({}));
      setWaStatus(res.ok ? '✓ WhatsApp message sent!' : '✗ ' + (data.error || 'Failed. Try again.'));
    } catch {
      setWaStatus('✗ Network error.');
    } finally {
      setWaSending(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-body" style={{ textAlign: 'center', padding: 36 }}>
        <div style={{
          width: 120, height: 120, borderRadius: '50%',
          background: 'linear-gradient(135deg,#4CCEE8,#0066CC)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px', boxShadow: '0 4px 20px rgba(0,102,204,.3)',
        }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{result?.score ?? 0}</div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,.75)', fontWeight: 500 }}>/{result?.totalMarks ?? 0}</div>
        </div>
        {grade && (
          <div style={{ marginBottom: 12 }}>
            <span className="badge" style={{ ...gradeStyle(grade), fontSize: 22, padding: '6px 24px', borderRadius: 12 }}>{grade}</span>
          </div>
        )}
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1a202c', marginBottom: 4 }}>{pct}%</div>
        <div style={{ fontSize: 13, color: '#718096', marginBottom: 24 }}>
          {result?.status === 'TIMED_OUT'
            ? <span style={{ color: '#c05621', fontWeight: 700 }}><i className="fas fa-clock"></i> Time Expired</span>
            : <span style={{ color: '#22863a', fontWeight: 700 }}><i className="fas fa-check-circle"></i> Submitted</span>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap', marginBottom: 24 }}>
          {[
            { label: 'Correct', value: result?.correctCount, color: '#22863a', bg: '#f0fff4' },
            { label: 'Wrong',   value: result?.wrongCount,   color: '#c53030', bg: '#fff5f5' },
            { label: 'Skipped', value: result?.skippedCount, color: '#718096', bg: '#f7fafc' },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: '12px 24px', minWidth: 80 }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value ?? '—'}</div>
              <div style={{ fontSize: 12, color: '#718096', fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>
        {/* Action buttons */}
        <div className="result-no-print" style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', marginBottom: emailOpen ? 16 : 0 }}>
          <button className="btn btn-primary" onClick={onBack}>
            <i className="fas fa-arrow-left"></i> {backLabel}
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => window.print()} title="Print or Save as PDF">
            <i className="fas fa-print"></i> Print / PDF
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => { setEmailOpen(o => !o); setEmailStatus(''); }} title="Email results">
            <i className="fas fa-envelope"></i> Email
          </button>
          <button
            onClick={() => { setWaOpen(o => !o); setWaStatus(''); setEmailOpen(false); setFullWaOpen(false); }}
            style={{ background: '#25D366', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            title="Send score summary via WhatsApp"
          >
            💬 WhatsApp
          </button>
          {result?.questions?.length > 0 && (
            <button
              onClick={() => { setFullWaOpen(o => !o); setFullWaStatus(''); setEmailOpen(false); setWaOpen(false); }}
              style={{ background: '#128C7E', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title="Send full review page via WhatsApp"
            >
              📋 Full Review
            </button>
          )}
        </div>
        {/* Email form */}
        {emailOpen && (
          <div className="result-no-print" style={{ maxWidth: 360, margin: '0 auto', textAlign: 'left' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="email"
                className="form-control"
                placeholder="Enter email address"
                value={emailAddr}
                onChange={e => setEmailAddr(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleEmail()}
                style={{ flex: 1 }}
              />
              <button className="btn btn-primary btn-sm" onClick={handleEmail} disabled={emailSending}>
                {emailSending ? 'Sending…' : 'Send'}
              </button>
            </div>
            {emailStatus && (
              <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: emailStatus.startsWith('✓') ? '#22863a' : '#c53030' }}>
                {emailStatus}
              </div>
            )}
          </div>
        )}
        {/* WhatsApp score summary form */}
        {waOpen && (
          <div className="result-no-print" style={{ maxWidth: 420, margin: '8px auto 0', textAlign: 'left' }}>
            <div style={{ fontSize: 11, color: '#718096', marginBottom: 4 }}>Sends a score summary message</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <WaPhoneInput
                country={waCountry} setCountry={setWaCountry}
                phone={waPhone} setPhone={setWaPhone}
                onEnter={handleWhatsApp}
                style={{ flex: 1 }}
              />
              <button
                onClick={handleWhatsApp}
                disabled={waSending}
                style={{ background: '#25D366', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                {waSending ? 'Sending…' : 'Send'}
              </button>
            </div>
            {waStatus && (
              <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: waStatus.startsWith('✓') ? '#22863a' : '#c53030' }}>
                {waStatus}
              </div>
            )}
          </div>
        )}
        {/* Full Review WhatsApp form */}
        {fullWaOpen && (
          <div className="result-no-print" style={{ maxWidth: 420, margin: '8px auto 0', textAlign: 'left' }}>
            <div style={{ fontSize: 11, color: '#718096', marginBottom: 4 }}>Generates a full review page and sends the link</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <WaPhoneInput
                country={fullWaCountry} setCountry={setFullWaCountry}
                phone={fullWaPhone} setPhone={setFullWaPhone}
                onEnter={handleFullReview}
                style={{ flex: 1 }}
              />
              <button
                onClick={handleFullReview}
                disabled={fullWaSending}
                style={{ background: '#128C7E', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                {fullWaSending ? 'Sending…' : 'Send'}
              </button>
            </div>
            {fullWaStatus && (
              <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: fullWaStatus.startsWith('✓') ? '#22863a' : '#c53030' }}>
                {fullWaStatus}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── REVIEW PANEL ──────────────────────────────────────────────────────────────
// Shows each question with the correct answer text AND the student's answer text.

function ReviewPanel({ questions }) {
  if (!questions || questions.length === 0)
    return <div style={{ textAlign: 'center', color: '#718096', padding: 32 }}>No review data available.</div>;

  return (
    <div>
      {questions.map((q, i) => {
        const correct      = q.isCorrect === 1 || q.isCorrect === true;
        const skipped      = !q.selectedOption;
        const correctTxt   = optionText(q, q.correctOption);
        const selectedTxt  = optionText(q, q.selectedOption);

        const dotColor  = correct ? '#22863a' : skipped ? '#a0aec0' : '#c53030';
        const rowBg     = correct ? '#f7fff9' : skipped ? '#fafafa' : '#fff8f8';
        const rowBorder = correct ? '#c6f6d5' : skipped ? '#e2e8f0' : '#fed7d7';

        return (
          <div key={i} style={{ border: `1px solid ${rowBorder}`, borderRadius: 10, padding: '14px 16px', marginBottom: 10, background: rowBg }}>

            {/* Question number + text */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <span style={{
                background: dotColor, color: '#fff', borderRadius: '50%',
                width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1,
              }}>{i + 1}</span>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1a202c', lineHeight: 1.55 }}>{q.questionText}</div>
            </div>

            {/* Two-column answer boxes */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginLeft: 36 }}>

              {/* Correct answer */}
              <div style={{ background: 'rgba(34,134,58,0.07)', border: '1px solid #c6f6d5', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#22863a', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  ✓ Correct Answer
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span style={{
                    background: '#22863a', color: '#fff', borderRadius: 4,
                    padding: '2px 8px', fontWeight: 800, fontSize: 13, flexShrink: 0,
                  }}>{q.correctOption}</span>
                  <span style={{ fontSize: 13, color: '#276749', lineHeight: 1.4 }}>{correctTxt || '—'}</span>
                </div>
              </div>

              {/* Student's answer */}
              <div style={{
                background: skipped ? 'rgba(160,174,192,0.07)' : correct ? 'rgba(34,134,58,0.07)' : 'rgba(197,48,48,0.07)',
                border: `1px solid ${skipped ? '#e2e8f0' : correct ? '#c6f6d5' : '#fed7d7'}`,
                borderRadius: 8, padding: '10px 12px',
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6,
                  color: skipped ? '#718096' : correct ? '#22863a' : '#c53030' }}>
                  {correct ? '✓ Your Answer' : skipped ? '— Not Answered' : '✗ Your Answer'}
                </div>
                {q.selectedOption ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <span style={{
                      background: correct ? '#22863a' : '#c53030', color: '#fff', borderRadius: 4,
                      padding: '2px 8px', fontWeight: 800, fontSize: 13, flexShrink: 0,
                    }}>{q.selectedOption}</span>
                    <span style={{ fontSize: 13, color: correct ? '#276749' : '#9b2c2c', lineHeight: 1.4 }}>{selectedTxt || '—'}</span>
                  </div>
                ) : (
                  <span style={{ fontSize: 13, color: '#a0aec0', fontStyle: 'italic' }}>Not answered</span>
                )}
              </div>

            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── RESULT VIEW (shown immediately after submitting) ──────────────────────────

function ResultView({ result, onBack, studentName }) {
  return (
    <div>
      <ScoreSummary result={result} onBack={onBack} studentName={studentName} />
      {result?.questions && result.questions.length > 0 && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">📋 Question Review</div>
            <span style={{ fontSize: 12, color: '#718096' }}>{result.questions.length} questions</span>
          </div>
          <div className="card-body">
            <ReviewPanel questions={result.questions} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── REVIEW VIEW (opened from My Results) ─────────────────────────────────────

function ReviewView({ reviewData, loading, error, onBack, studentName }) {
  return (
    <div>
      {(loading || (error && !reviewData)) && (
        <div style={{ marginBottom: 16 }}>
          <button className="btn btn-outline btn-sm" onClick={onBack}>
            <i className="fas fa-arrow-left"></i> Back to My Results
          </button>
        </div>
      )}
      {loading && <div className="loading-msg">Loading review…</div>}
      {error  && <div className="error-msg">{error}</div>}
      {reviewData && !loading && (
        <>
          <ScoreSummary result={reviewData} onBack={onBack} backLabel="Back to My Results" studentName={studentName} />
          <div className="card">
            <div className="card-header">
              <div className="card-title">📋 Question Review — {reviewData.testName}</div>
              <span style={{ fontSize: 12, color: '#718096' }}>{reviewData.questions?.length ?? 0} questions</span>
            </div>
            <div className="card-body">
              <ReviewPanel questions={reviewData.questions} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

export default function StudentPortal() {
  const [view, setView]               = useState('loading');  // 'loading'|'dashboard'|'taking'|'result'|'review'
  const [student, setStudent]         = useState(null);
  const [loadErr, setLoadErr]         = useState('');
  const [currentAttemptId, setCurrentAttemptId] = useState(null);
  const [result, setResult]           = useState(null);
  const [reviewData, setReviewData]   = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');

  useEffect(() => {
    testStudentApi.getMe()
      .then(data => { setStudent(data); setView('dashboard'); })
      .catch(e => { setLoadErr(e.message); setView('dashboard'); });
  }, []);

  const handleStartTest = useCallback((attemptId) => {
    setCurrentAttemptId(attemptId);
    setView('taking');
  }, []);

  const handleFinish = useCallback((res) => {
    if (!res) { setCurrentAttemptId(null); setView('dashboard'); return; }
    setResult(res);
    setView('result');
  }, []);

  const handleBackToDashboard = useCallback(() => {
    setResult(null);
    setCurrentAttemptId(null);
    setReviewData(null);
    setReviewError('');
    setView('dashboard');
  }, []);

  const handleReview = useCallback((attemptId) => {
    setReviewData(null);
    setReviewError('');
    setReviewLoading(true);
    setView('review');
    testStudentApi.getAttemptReview(attemptId)
      .then(data => setReviewData(data))
      .catch(e => setReviewError(e.message))
      .finally(() => setReviewLoading(false));
  }, []);

  if (view === 'loading') return <div className="loading-msg">Loading student portal…</div>;

  return (
    <div>
      {loadErr && (
        <div className="error-msg" style={{ marginBottom: 16 }}>
          <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i>{loadErr}
        </div>
      )}
      {view === 'dashboard' && (
        <DashboardView student={student} onStartTest={handleStartTest} onReview={handleReview} />
      )}
      {view === 'taking' && currentAttemptId && (
        <TakeTest attemptId={currentAttemptId} onFinish={handleFinish} />
      )}
      {view === 'result' && result && (
        <ResultView result={result} onBack={handleBackToDashboard} studentName={student?.name} />
      )}
      {view === 'review' && (
        <ReviewView
          reviewData={reviewData}
          loading={reviewLoading}
          error={reviewError}
          onBack={handleBackToDashboard}
          studentName={student?.name}
        />
      )}
    </div>
  );
}
